import { useState, useCallback, useContext, useMemo, useEffect, useRef } from 'react';
import { useField, useForm } from 'react-final-form';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import useEmployeesInfinite from '@ubeya/shared/hooks/employees/useEmployeesInfinite';
import useEmployee from '@ubeya/shared/hooks/employees/useEmployee';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { getEmployeeDisplayName } from '@ubeya/shared-web/utils/employee';
import useDebounce from '@ubeya/shared/hooks/useDebounce';

// The first of the employee's branches the admin can access (others 403 on submit).
const firstAccessibleBranchId = (branchIds, branchesOptions) =>
  (branchIds || []).find((id) => branchesOptions.some((branch) => branch.value === id)) ?? null;

const useTicketCreationEmployeeFields = ({ onChange, onResetAttachments, lockedEmployeeId }) => {
  const [employeeSearch, setEmployeeSearch] = useState('');
  const debouncedSearch = useDebounce(employeeSearch, 300);

  const {
    data: employees,
    isLoading: employeesLoading,
    fetchMore,
    canFetchMore,
    isFetchingMore
  } = useEmployeesInfinite({ activeTab: 'active', search: debouncedSearch, pageSize: 20 });

  const { branchesOptions } = useBranches();
  const { isShowLastNameFirst = false } = useContext(ConfigContext);

  const { data: lockedEmployee } = useEmployee({ employeeId: lockedEmployeeId, enabled: !!lockedEmployeeId });
  const lockedOption = useMemo(() => {
    if (!lockedEmployee) return null;
    return {
      value: lockedEmployee.id,
      label: getEmployeeDisplayName(lockedEmployee.firstName, lockedEmployee.lastName, isShowLastNameFirst),
      branchIds: (lockedEmployee.branches || [])
        .map((branch) => branch?.id ?? branch)
        .concat(lockedEmployee.branchId != null ? [lockedEmployee.branchId] : [])
    };
  }, [lockedEmployee, isShowLastNameFirst]);

  const employeeOptions = useMemo(
    () =>
      (employees || []).map((employee) => ({
        value: employee.id,
        label: getEmployeeDisplayName(employee.firstName, employee.lastName, isShowLastNameFirst),
        firstName: employee.firstName,
        lastName: employee.lastName,
        image: employee.image,
        branchIds: (employee.branches || [])
          .map((branch) => branch?.id ?? branch)
          .concat(employee.branchId != null ? [employee.branchId] : [])
      })),
    [employees, isShowLastNameFirst]
  );

  const { input: employeeInput } = useField('employeeId');
  const { input: employeeNameInput } = useField('employeeName');
  const { input: branchInput } = useField('branchId');
  const form = useForm();

  // Captured at selection so the chosen value survives a search that drops it from the loaded page.
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const resetClassification = useCallback(() => {
    form.batch(() => {
      form.change('categoryId', null);
      form.change('assignedAdminId', null);
      form.change('shiftId', null);
      form.change('shiftDetails', null);
    });
    onChange({ categoryId: null, assignedAdminId: null, shiftId: null, shiftDetails: null });
    onResetAttachments();
  }, [form, onChange, onResetAttachments]);

  const employeeOption = useMemo(() => {
    if (!employeeInput.value) return null;
    const storedOption = selectedEmployee?.value === employeeInput.value ? selectedEmployee : null;
    return (
      employeeOptions.find((option) => option.value === employeeInput.value) ||
      storedOption || { value: employeeInput.value, label: employeeNameInput.value || '' }
    );
  }, [employeeInput.value, employeeNameInput.value, employeeOptions, selectedEmployee]);

  const branchOptions = useMemo(
    () => branchesOptions.filter((branch) => (employeeOption?.branchIds || []).includes(branch.value)),
    [branchesOptions, employeeOption]
  );

  const selectedBranchOption = useMemo(
    () => (branchInput.value ? branchesOptions.find((branch) => branch.value === branchInput.value) ?? null : null),
    [branchInput.value, branchesOptions]
  );

  const selectEmployee = useCallback(
    (option) => {
      setSelectedEmployee(option);
      employeeInput.onChange(option?.value ?? null);
      employeeNameInput.onChange(option?.label || '');
      branchInput.onChange(firstAccessibleBranchId(option?.branchIds, branchesOptions));
    },
    [employeeInput, employeeNameInput, branchInput, branchesOptions]
  );

  const handleEmployeeChange = useCallback(
    (value) => {
      selectEmployee(employeeOptions.find((option) => option.value === value) || null);
      resetClassification();
    },
    [selectEmployee, employeeOptions, resetClassification]
  );

  const handleBranchChange = useCallback(
    (value) => {
      branchInput.onChange(value);
      resetClassification();
    },
    [branchInput, resetClassification]
  );

  // Pre-select the locked employee once it and the branch list have loaded.
  const didSeedLocked = useRef(false);
  useEffect(() => {
    if (didSeedLocked.current || !lockedOption || branchesOptions.length === 0) return;
    didSeedLocked.current = true;
    selectEmployee(lockedOption);
  }, [lockedOption, branchesOptions, selectEmployee]);

  return {
    employeeOptions,
    employeeOption,
    branchOptions,
    selectedBranchOption,
    isEmployeeSelected: !!employeeInput.value,
    isEmployeeLocked: !!lockedEmployeeId,
    employeesLoading,
    fetchMore,
    canFetchMore,
    isFetchingMore,
    setEmployeeSearch,
    handleEmployeeChange,
    handleBranchChange
  };
};

export default useTicketCreationEmployeeFields;
